import { Button } from '@brightdrop/bd-ui';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import CloseGlyph from '../icons/CloseGlyph';
import useStyles from './MessageFeedbackInlineForm.styles';

const SUBMIT_BUTTON_WIDTH = '118px';
const TEXTAREA_MIN_ROWS = 6;
const SUBMIT_BUTTON_SX = (theme: Theme) => ({
  borderRadius: theme.new?.shape.borderRadius.small ?? '0.5rem',
  fontWeight: 500,
  textTransform: 'none' as const,
});

/** Props for the inline freeform feedback form. */
export type MessageFeedbackInlineFormProps = {
  /**
   * Id of the assistant message this comment is attached to. Forwarded
   * to `onSubmit` and used to disambiguate test selectors across
   * sibling rows.
   */
  messageId: string;
  /**
   * Fired with the trimmed comment when the user submits. May return
   * a promise; the form keeps the submitting state until the promise
   * resolves and surfaces a toast on success or rejection.
   */
  onSubmit: (messageId: string, comment: string) => void | Promise<void>;
  /**
   * Fired when the user dismisses the form via the close glyph or
   * after a successful submission. The parent owns whether the
   * `MessageFeedbackRow` re-renders without the inline form.
   */
  onClose: () => void;
  /**
   * Called when submission succeeds. Lets the row deselect the
   * overflow icon and dispatch follow-up state separately from the
   * close affordance.
   */
  onSubmitSuccess?: (messageId: string, comment: string) => void;
  /** Called when submission throws/rejects. */
  onSubmitError?: (error: unknown) => void;
};

/**
 * Inline freeform feedback card that expands directly below the
 * `MessageFeedbackRow` once a user activates the overflow control on
 * a rated assistant message.
 *
 * Mirrors the GA-Sidekick Figma source for the empty
 * (`16166:21117`), filled (`16166:21118`) and submitting
 * (`16166:21119`) states:
 *
 * - Card chrome from `useStyles().card` (400×, hairline border,
 *   12px radius and padding, 16px gap, right-aligned actions).
 * - Title row with the localized `chat.feedback.form.title` and a
 *   16x16 close glyph that doubles as the form's dismiss affordance.
 * - Multiline `TextField` with the theme-level outlined-input
 *   overrides (8px radius, brand-blue focus border) plus a local
 *   brand-100 focus ring shadow to match the Figma `Focused` state.
 * - Submit `Button` from `@brightdrop/bd-ui`, sized to 118px,
 *   disabled while empty or in-flight, and rendering the canonical
 *   loader icon via the `isLoading` prop while the submission
 *   promise is pending.
 *
 * The form intentionally keeps its draft, submitting flag and
 * focus state local. Per design annotation `16166:25718`, dismissing
 * the form discards any unsent draft; re-opening the form via the
 * overflow icon yields an empty textarea.
 */
function MessageFeedbackInlineForm({
  messageId,
  onSubmit,
  onClose,
  onSubmitSuccess,
  onSubmitError,
}: MessageFeedbackInlineFormProps) {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const [value, setValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmed = value.trim();
  const canSubmit = !isSubmitting && trimmed.length > 0;

  // The form's section needs both a stable DOM `id` (so the
  // overflow toggle's `aria-controls` resolves to a real element)
  // and a labelled `aria-labelledby` association so the implicit
  // `region` role on the `<section>` gets the title as its
  // accessible name. Both ids are scoped by `messageId` to stay
  // unique across sibling assistant rows.
  const formId = `ai-sidekick-feedback-form-${messageId}`;
  const titleId = `ai-sidekick-feedback-form-title-${messageId}`;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(event.target.value);
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || trimmed.length === 0) {
      return;
    }
    setIsSubmitting(true);
    try {
      await Promise.resolve(onSubmit(messageId, trimmed));
      onSubmitSuccess?.(messageId, trimmed);
      onClose();
    } catch (error) {
      onSubmitError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    trimmed,
    onSubmit,
    onSubmitSuccess,
    onSubmitError,
    onClose,
    messageId,
  ]);

  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    onClose();
  }, [isSubmitting, onClose]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        void handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <Box
      component="section"
      id={formId}
      aria-labelledby={titleId}
      className={classes.card}
      data-testid={formId}
    >
      <Box className={classes.textareaGroup}>
        <Box className={classes.titleRow}>
          <Typography
            id={titleId}
            variant="small_body_medium"
            component="h3"
            className={classes.title}
          >
            {t('chat.feedback.form.title')}
          </Typography>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label={t('chat.feedback.form.closeAriaLabel')}
            data-testid={`ai-sidekick-feedback-form-close-${messageId}`}
          >
            <CloseGlyph size={16} />
          </IconButton>
        </Box>
        <TextField
          className={classes.textfield}
          multiline
          minRows={TEXTAREA_MIN_ROWS}
          maxRows={TEXTAREA_MIN_ROWS}
          autoFocus
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={isSubmitting}
          placeholder={t('chat.feedback.form.placeholder')}
          slotProps={{
            input: {
              sx: { padding: 0 },
            },
            htmlInput: {
              'aria-label': t('chat.feedback.form.textareaAriaLabel'),
              'data-testid': `ai-sidekick-feedback-form-input-${messageId}`,
              // Keep edge declarations explicit: this slot does not expose
              // shorthand padding as computed edge styles.
              sx: {
                paddingTop: 1.5,
                paddingRight: 1.5,
                paddingBottom: 1.5,
                paddingLeft: 1.5,
              },
            },
          }}
        />
      </Box>
      <Button
        variant="primary"
        size="medium"
        width={SUBMIT_BUTTON_WIDTH}
        sx={SUBMIT_BUTTON_SX}
        isLoading={isSubmitting}
        onClick={handleSubmit}
        disabled={!canSubmit}
        aria-label={
          isSubmitting
            ? t('chat.feedback.form.submitLoadingAriaLabel')
            : undefined
        }
        data-testid={`ai-sidekick-feedback-form-submit-${messageId}`}
      >
        {t('chat.feedback.form.submitLabel')}
      </Button>
    </Box>
  );
}

export default MessageFeedbackInlineForm;
